import { useEffect, useState } from "react";
import { Buton, CizgiGrafik, marka } from "@koclukpanel/ui";
import type { DenemeHedefFarki, DenemeSonucCiktisi } from "@koclukpanel/contracts";

export type KarneIstegi = <T>(yol: string) => Promise<T>;
interface KarneSatiri {
  atamaId: string;
  ad: string;
  tarih: string;
  net: number;
  tur: string;
  hedefNet?: DenemeHedefFarki | null;
}
export interface KarneVerisi {
  karne: DenemeSonucCiktisi;
  trend: KarneSatiri[];
  dersTrendi: Array<{ ad: string; noktalar: Array<{ ad: string; tarih: string; net: number }> }>;
}
const sayi = (deger: number) => deger.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const yol = (ogrenciId: string) => `/denemeler/ogrenci/${encodeURIComponent(ogrenciId)}/karne`;

export function useKarneAcik(istek: KarneIstegi, etkin = true): boolean {
  const [acik, setAcik] = useState(false);
  useEffect(() => {
    let gecerli = true;
    setAcik(false);
    if (etkin)
      void istek<{ acik: boolean }>("/denemeler/karne/durum").then(
        (veri) => {
          if (gecerli) setAcik(veri.acik === true);
        },
        () => {},
      );
    return () => {
      gecerli = false;
    };
  }, [istek, etkin]);
  return etkin && acik;
}

/** F-EXM-25 — aynı bileşen öğrenci, veli ve panelde kendi oturum istemcisiyle açılır. */
export function Karne({ ogrenciId, istek }: { ogrenciId: string; istek: KarneIstegi }) {
  const acik = useKarneAcik(istek);
  return acik ? <KarneListesi key={ogrenciId} ogrenciId={ogrenciId} istek={istek} /> : null;
}

function KarneListesi({ ogrenciId, istek }: { ogrenciId: string; istek: KarneIstegi }) {
  const [liste, setListe] = useState<KarneSatiri[] | null>(null);
  const [secili, setSecili] = useState<string | null>(null);
  const [hata, setHata] = useState(false);
  const [yenileme, setYenileme] = useState(0);
  useEffect(() => {
    let gecerli = true;
    setHata(false);
    setListe(null);
    void istek<KarneSatiri[]>(yol(ogrenciId)).then(
      (veri) => {
        if (gecerli) setListe(veri);
      },
      () => {
        if (gecerli) setHata(true);
      },
    );
    return () => {
      gecerli = false;
    };
  }, [ogrenciId, istek, yenileme]);
  if (hata)
    return (
      <p role="alert">
        Karneler alınamadı. <Buton onClick={() => setYenileme((s) => s + 1)}>Tekrar dene</Buton>
      </p>
    );
  if (!liste) return <p role="status">Karneler yükleniyor…</p>;
  return (
    <section aria-label="Karneler" className="min-w-0 space-y-3">
      <h2 className="font-semibold">Karneler</h2>
      {liste.length === 0 && <p>Henüz sonuçlanmış deneme yok.</p>}
      <div className="flex flex-wrap gap-2">
        {liste.map((satir) => (
          <Buton
            key={satir.atamaId}
            className="max-w-full [overflow-wrap:anywhere] break-words whitespace-normal"
            onClick={() => setSecili(satir.atamaId)}
          >
            Karne: {satir.ad}
          </Buton>
        ))}
      </div>
      {secili && (
        <KarneAyrintisi key={secili} ogrenciId={ogrenciId} atamaId={secili} istek={istek} />
      )}
    </section>
  );
}

function KarneAyrintisi({
  ogrenciId,
  atamaId,
  istek,
}: {
  ogrenciId: string;
  atamaId: string;
  istek: KarneIstegi;
}) {
  const [veri, setVeri] = useState<KarneVerisi | null>(null);
  const [hata, setHata] = useState(false);
  const [yenileme, setYenileme] = useState(0);
  const adres = `${yol(ogrenciId)}/${encodeURIComponent(atamaId)}`;
  useEffect(() => {
    let gecerli = true;
    setHata(false);
    setVeri(null);
    void istek<KarneVerisi>(adres).then(
      (sonuc) => {
        if (gecerli) setVeri(sonuc);
      },
      () => {
        if (gecerli) setHata(true);
      },
    );
    return () => {
      gecerli = false;
    };
  }, [adres, istek, yenileme]);
  if (hata)
    return (
      <p role="alert">
        Karne alınamadı. <Buton onClick={() => setYenileme((s) => s + 1)}>Tekrar dene</Buton>
      </p>
    );
  if (!veri) return <p role="status">Karne yükleniyor…</p>;
  return (
    <>
      <a className="inline-flex min-h-11 items-center underline" href={`/api/v1${adres}/pdf`}>
        Karneyi PDF indir
      </a>
      <KarneGorunumu veri={veri} />
    </>
  );
}

export function KarneGorunumu({ veri }: { veri: KarneVerisi }) {
  const { karne, trend, dersTrendi } = veri;
  const tabloSinifi = "w-full table-fixed text-left text-xs [overflow-wrap:anywhere]";
  return (
    <article
      aria-label="Deneme karnesi"
      style={{ minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}
      className="space-y-4 rounded-xl border p-3"
    >
      <header>
        <p>{marka.ad}</p>
        <h2 className="text-lg font-semibold">{karne.ad}</h2>
        <p>
          {[
            karne.yayinevi,
            karne.seriesName,
            new Date(karne.examDate).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </header>
      {karne.duzeltmeBekliyor && <p role="status">Düzeltiliyor</p>}
      <p>Toplam net: {sayi(karne.toplamNet)}</p>
      <table className={tabloSinifi}>
        <caption className="text-left font-semibold">Ders netleri</caption>
        <thead>
          <tr>
            {["Ders", "D", "Y", "B", "Net"].map((baslik) => (
              <th key={baslik} scope="col">
                {baslik}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {karne.dersler.map((ders) => (
            <tr key={ders.sectionId}>
              <th scope="row">{ders.ad}</th>
              <td>{ders.dogru}</td>
              <td>{ders.yanlis}</td>
              <td>{ders.bos}</td>
              <td>{sayi(ders.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {karne.kazanimlar && (
        <table className={tabloSinifi}>
          <caption className="text-left font-semibold">Kazanım analizi</caption>
          <thead>
            <tr>
              <th scope="col">Kazanım</th>
              <th scope="col">Doğru</th>
              <th scope="col">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {karne.kazanimlar.map((satir) => (
              <tr key={satir.outcome}>
                <th scope="row">{satir.outcome}</th>
                <td>{satir.dogru}</td>
                <td>{satir.toplam}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="font-semibold">Gelişim grafiği</h3>
      <CizgiGrafik
        baslik="Gelişim grafiği"
        birim="net"
        ozet="Aynı sınav türündeki son beş deneme."
        noktalar={trend.map((satir) => ({
          tarih: satir.tarih.slice(0, 10),
          degerler: { net: satir.net, hedef: satir.hedefNet?.toplamHedef ?? null },
        }))}
        seriler={[
          { anahtar: "net", etiket: "Toplam net", ton: "marka" },
          { anahtar: "hedef", etiket: "Hedef net", ton: "notr" },
        ]}
      />
      {trend.map(
        (satir) =>
          satir.hedefNet && (
            <p key={satir.atamaId}>
              {satir.ad}: hedef {sayi(satir.hedefNet.toplamHedef)}, fark{" "}
              {sayi(satir.hedefNet.toplamFark)}
            </p>
          ),
      )}
      {dersTrendi.map((ders) => (
        <CizgiGrafik
          key={ders.ad}
          baslik={`${ders.ad} gelişimi`}
          birim="net"
          ozet="Ders bazlı net gelişimi."
          noktalar={ders.noktalar.map((nokta) => ({
            tarih: nokta.tarih.slice(0, 10),
            degerler: { net: nokta.net },
          }))}
          seriler={[{ anahtar: "net", etiket: ders.ad, ton: "marka" }]}
        />
      ))}
      {/* F-EXM-12/20/22 — istemci yalnız sunucunun gönderdiği alanları gösterir. */}
      {karne.sorular ? (
        <table className={tabloSinifi}>
          <caption className="text-left font-semibold">Soru tablosu</caption>
          <thead>
            <tr>
              {["Ders / soru", "Cevabın", "Doğru cevap", "Sonuç"].map((baslik) => (
                <th key={baslik} scope="col">
                  {baslik}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {karne.sorular.map((soru) => (
              <tr key={`${soru.sectionId}-${soru.questionNo}`}>
                <th scope="row">
                  {karne.dersler.find((ders) => ders.sectionId === soru.sectionId)?.ad} /{" "}
                  {soru.questionNo}
                </th>
                <td>{soru.cevap ?? "Boş"}</td>
                <td>{soru.dogruCevap}</td>
                <td>
                  {{ DOGRU: "Doğru", YANLIS: "Yanlış", BOS: "Boş", IPTAL: "İptal" }[soru.sonuc]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>Soru tablosu henüz açık değil.</p>
      )}
      {karne.rank !== undefined && <p>Kurum içi sıralama: {karne.rank}</p>}
      {karne.percentile !== undefined && <p>Yüzdelik dilim: %{sayi(karne.percentile * 100)}</p>}
    </article>
  );
}
